import dayjs from '~/timeInit';

import {
  CONDITION_ICON_OPTIONS,
  SOCIETE_MATCH_TARGET_TIERS,
  SUCCESS_STEP_DEFINITIONS,
} from './constants';
import { isValidDateTimeInput } from '../dateTime';
import { hasNonEmptyString, isKnownValue } from '../parseHelpers';
import { findProgramByName } from './programs';
import { isSiteRegionCode } from '../regions';
import { isValidCampaignSlug } from './slug';
import type { PayloadValidationResult } from '../types';
import type { ChallengeCardBackground, StatusCampaignPayloadBuilderState, ProgramConfig } from './types';
import { validateRequiredString } from '../validationHelpers';

const CONDITION_ICON_VALUES = CONDITION_ICON_OPTIONS.map((option) => option.value);
const CHALLENGE_CARD_BACKGROUND_TYPES = ['color', 'tierGradient', 'image'] as const;

export function allStringsNonEmpty(values: Array<string>): boolean {
  return values.length > 0 && values.every((value) => value.trim());
}

function validateCampaignFields(state: StatusCampaignPayloadBuilderState, errors: Array<string>) {
  validateRequiredString(state.campaignId, 'Campaign ID', errors);
  validateRequiredString(state.slug, 'Campaign URL Slug', errors);
  if (state.slug.trim() && !isValidCampaignSlug(state.slug)) {
    errors.push('Campaign URL Slug must be lowercase letters, numbers, and hyphens');
  }
  validateRequiredString(state.termsSlug, 'Campaign Terms Slug', errors);

  if ((state.allowedRegions?.length ?? 0) === 0) {
    errors.push('Campaign Allowed Regions requires at least one region');
  }
  const invalidRegions = (state.allowedRegions ?? []).filter((region) => !isSiteRegionCode(region));
  if (invalidRegions.length > 0) {
    errors.push(`Campaign Allowed Regions has invalid codes: ${invalidRegions.join(', ')}`);
  }

  validateRequiredString(state.applicationPeriodFrom, 'Application Opens', errors);
  validateRequiredString(state.applicationPeriodTo, 'Application Closes', errors);
  const hasStartAt = hasNonEmptyString(state.applicationPeriodFrom);
  const hasEndAt = hasNonEmptyString(state.applicationPeriodTo);
  if (hasStartAt && !isValidDateTimeInput(state.applicationPeriodFrom)) {
    errors.push('Application Opens must be a valid datetime');
  }
  if (hasEndAt && !isValidDateTimeInput(state.applicationPeriodTo)) {
    errors.push('Application Closes must be a valid datetime');
  }
  if (
    hasStartAt &&
    hasEndAt &&
    isValidDateTimeInput(state.applicationPeriodFrom) &&
    isValidDateTimeInput(state.applicationPeriodTo) &&
    dayjs(state.applicationPeriodTo).isBefore(dayjs(state.applicationPeriodFrom))
  ) {
    errors.push('Application Closes must be the same as or after Application Opens');
  }
}

function validateHero(state: StatusCampaignPayloadBuilderState, errors: Array<string>) {
  validateRequiredString(state.heroTitle, 'Hero Title', errors);
  validateRequiredString(state.heroSubtitle, 'Hero Subtitle', errors);
  validateRequiredString(state.heroDescription, 'Hero Description', errors);
  validateRequiredString(state.heroCtaText, 'Hero CTA Text', errors);
  validateRequiredString(state.heroImageId, 'Hero Image ID', errors);
}

function validatePrograms(programs: Array<ProgramConfig>, errors: Array<string>) {
  if (programs.length === 0) {
    errors.push('Programs requires at least one program');
    return;
  }

  programs.forEach((program, programIndex) => {
    const label = `Program ${programIndex + 1}`;
    const programName = program.programName.trim();
    validateRequiredString(program.programName, `${label} Program`, errors);

    const definition = programName ? findProgramByName(programName) : undefined;
    if (programName && !definition) {
      errors.push(`${label} Program must be a known loyalty program`);
    }

    validateRequiredString(program.logoImageId, `${label} Logo Image ID`, errors);

    if (program.tierMappings.length === 0) {
      errors.push(`${label} Tier Mapping requires at least one mapping`);
    }

    program.tierMappings.forEach((mapping, mappingIndex) => {
      const mappingLabel = `${label} Tier Mapping ${mappingIndex + 1}`;
      validateRequiredString(mapping.sourceTier, `${mappingLabel} Source Tier`, errors);
      validateRequiredString(mapping.targetSocieteTier, `${mappingLabel} Société Tier`, errors);

      if (mapping.sourceTier.trim() && definition && !definition.tiers.includes(mapping.sourceTier)) {
        errors.push(`${mappingLabel} Source Tier must be a valid tier for ${programName || 'the selected program'}`);
      }
      if (
        mapping.targetSocieteTier.trim() &&
        !isKnownValue(mapping.targetSocieteTier, SOCIETE_MATCH_TARGET_TIERS)
      ) {
        errors.push(`${mappingLabel} Société Tier must be Silver, Gold, or Platinum`);
      }
    });
  });
}

function validateConditions(state: StatusCampaignPayloadBuilderState, errors: Array<string>) {
  if (state.conditions.length === 0) {
    errors.push('Conditions requires at least one condition');
    return;
  }
  if (state.conditions.length > 4) {
    errors.push('Conditions must have no more than 4 conditions');
  }

  state.conditions.forEach((condition, index) => {
    const label = `Condition ${index + 1}`;
    if (!isKnownValue(condition.icon, CONDITION_ICON_VALUES)) {
      errors.push(`${label} Icon must be a known condition icon`);
    }
    validateRequiredString(condition.title, `${label} Title`, errors);
    validateRequiredString(condition.description, `${label} Description`, errors);
  });
}

function validateTextSteps(
  steps: Array<{ title: string; description: string }>,
  emptyError: string,
  rowLabel: string,
  errors: Array<string>,
) {
  if (steps.length === 0) {
    errors.push(emptyError);
    return;
  }

  steps.forEach((step, index) => {
    const label = `${rowLabel} ${index + 1}`;
    validateRequiredString(step.title, `${label} Title`, errors);
    validateRequiredString(step.description, `${label} Description`, errors);
  });
}

function validateChallengeCard(state: StatusCampaignPayloadBuilderState, errors: Array<string>) {
  validateRequiredString(state.challengeCardTitleSuffix, 'Challenge Card Title Suffix', errors);
  validateRequiredString(state.challengeCardSubtitleBefore, 'Challenge Card Subtitle Before', errors);
  validateRequiredString(state.challengeCardSubtitleAfter, 'Challenge Card Subtitle After', errors);

  const background = state.challengeCardBackground;
  if (!isKnownValue(background.type, CHALLENGE_CARD_BACKGROUND_TYPES)) {
    errors.push('Challenge Card Background Type must be color, tierGradient, or image');
    return;
  }
  if (background.type === 'color') {
    validateRequiredString(background.value ?? '', 'Challenge Card Background Color', errors);
  }
  if (background.type === 'image') {
    validateRequiredString(background.imageId ?? '', 'Challenge Card Background Image ID', errors);
  }
}

function validateStringRows(values: Array<string>, label: string, errors: Array<string>) {
  if (values.length === 0) {
    errors.push(`${label} requires at least one value`);
    return;
  }

  values.forEach((value, index) => {
    if (!hasNonEmptyString(value)) {
      errors.push(`${label} row ${index + 1} is required`);
    }
  });
}

function validateEvidence(state: StatusCampaignPayloadBuilderState, errors: Array<string>) {
  if (state.evidenceMinPhotos <= 0) {
    errors.push('Evidence Minimum Photos must be greater than 0');
  }
  validateRequiredString(state.evidenceVerificationImageId, 'Evidence Verification Image ID', errors);
  validateStringRows(state.evidencePhotoHints, 'Evidence Photo Hints', errors);
  validateStringRows(state.evidenceAcceptedFormats, 'Evidence Accepted Formats', errors);
  validateStringRows(state.evidenceProofRequirements, 'Evidence Proof Requirements', errors);
}

function validateSuccessSteps(state: StatusCampaignPayloadBuilderState, errors: Array<string>) {
  SUCCESS_STEP_DEFINITIONS.forEach((definition) => {
    const step = state.successSteps.find((candidate) => candidate.key === definition.key);
    if (!step) {
      errors.push(`Success Steps must include ${definition.key}`);
      return;
    }
    validateRequiredString(step.title, `Success Step ${definition.key} Title`, errors);
    validateRequiredString(step.description, `Success Step ${definition.key} Description`, errors);
  });

  const unknownKeys = state.successSteps
    .map((step) => step.key)
    .filter((key) => !SUCCESS_STEP_DEFINITIONS.some((definition) => definition.key === key));
  if (unknownKeys.length > 0) {
    errors.push(`Success Steps contains unknown keys: ${unknownKeys.join(', ')}`);
  }
}

function validateFaqs(state: StatusCampaignPayloadBuilderState, errors: Array<string>) {
  state.faqs.forEach((faq, index) => {
    const label = `FAQ ${index + 1}`;
    const hasQuestion = hasNonEmptyString(faq.question);
    const hasAnswer = hasNonEmptyString(faq.answer);
    if (hasQuestion && !hasAnswer) {
      errors.push(`${label} Answer is required when ${label} Question is filled`);
    }
    if (!hasQuestion && hasAnswer) {
      errors.push(`${label} Question is required when ${label} Answer is filled`);
    }
  });
}

export function validateStatusMatchPayload(state: StatusCampaignPayloadBuilderState): PayloadValidationResult {
  const errors: Array<string> = [];

  validateCampaignFields(state, errors);
  validateHero(state, errors);
  validatePrograms(state.programs, errors);
  validateConditions(state, errors);
  validateTextSteps(state.howItWorks, 'How It Works requires at least one step', 'How It Works Step', errors);
  validateChallengeCard(state, errors);
  validateEvidence(state, errors);
  validateSuccessSteps(state, errors);
  validateFaqs(state, errors);

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function hasValidStatusMatchPrograms(programs: Array<ProgramConfig>): boolean {
  return programs.some(
    (program) =>
      program.programName.trim() &&
      program.logoImageId.trim() &&
      program.tierMappings.some((mapping) => mapping.sourceTier.trim() && mapping.targetSocieteTier.trim()),
  );
}

export function hasValidStatusMatchConditions(state: StatusCampaignPayloadBuilderState): boolean {
  return (
    state.conditions.length > 0 &&
    state.conditions.every((condition) => condition.title.trim() && condition.description.trim())
  );
}

export function hasValidStatusMatchTextSteps(steps: Array<{ title: string; description: string }>): boolean {
  return steps.length > 0 && steps.every((step) => step.title.trim() && step.description.trim());
}

export function hasValidStatusMatchChallengeCard(state: StatusCampaignPayloadBuilderState): boolean {
  if (
    !state.challengeCardTitleSuffix.trim() ||
    !state.challengeCardSubtitleBefore.trim() ||
    !state.challengeCardSubtitleAfter.trim()
  ) {
    return false;
  }

  return hasValidChallengeCardBackground(state.challengeCardBackground);
}

export function hasValidChallengeCardBackground(background: ChallengeCardBackground): boolean {
  if (background.type === 'color') {
    return Boolean(background.value?.trim());
  }
  if (background.type === 'image') {
    return Boolean(background.imageId?.trim());
  }
  return true;
}

export function hasValidStatusMatchEvidence(state: StatusCampaignPayloadBuilderState): boolean {
  return (
    state.evidenceMinPhotos > 0 &&
    state.evidenceVerificationImageId.trim() &&
    allStringsNonEmpty(state.evidencePhotoHints) &&
    allStringsNonEmpty(state.evidenceAcceptedFormats) &&
    allStringsNonEmpty(state.evidenceProofRequirements)
  );
}

export function hasValidStatusMatchCampaignFields(state: StatusCampaignPayloadBuilderState): boolean {
  return Boolean(
    state.termsSlug.trim() &&
      state.applicationPeriodFrom.trim() &&
      state.applicationPeriodTo.trim() &&
      (state.allowedRegions?.length ?? 0) > 0,
  );
}

export function hasValidStatusMatchHero(state: StatusCampaignPayloadBuilderState): boolean {
  return Boolean(
    state.heroTitle.trim() &&
      state.heroSubtitle.trim() &&
      state.heroDescription.trim() &&
      state.heroCtaText.trim() &&
      state.heroImageId.trim(),
  );
}

export function isStatusMatchPayloadComplete(state: StatusCampaignPayloadBuilderState): boolean {
  return validateStatusMatchPayload(state).isValid;
}
